/**
 * AuthorPostEditorDialog — manual-create / edit dialog for author_posts/*.
 *
 * Concurrent Minsky refactor (2026-04-29) replacement for PostExampleEditorDialog.
 * Targets the unified Post collection at:
 *   projects/{pid}/{authors|external_authors}/{authorId}/platforms/{platformId}/author_posts/{postId}
 *
 * Three contexts of use:
 *   1. Manual-add from PlatformPostsTable toolbar — opens with empty post,
 *      caller knows authorId/platformId from row context.
 *   2. Edit existing scraped/manual author_post — caller passes existing post.
 *   3. Link-as-example from TopicDetailPanel Examples sub-section — initial
 *      `exampleForTopicIds: [topic.id]` so the post is curated on save.
 *
 * Wraps UnifiedPostDialog with an AuthorPost ↔ PostLike adapter (kept inline
 * to avoid extending the retired postLike.ts helper).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import {
  authorContentCorpusService,
  externalAuthorContentCorpusService,
  type CreateManualPostInput,
} from '../../services/AuthorContentCorpusService';
import { UnifiedPostDialog } from '../postLike/UnifiedPostDialog';
import type { PostLike } from '../../utils/postLike';
import type { Post, PostAuthorBasePath } from '../../types';
import type { GenerateImageConfig, GenerateImageResult } from '../postExamples/AiAssetsEditor';

export interface AuthorPostEditorDialogProps {
  open: boolean;
  onClose: () => void;

  projectId: string;
  userId: string;
  authorId: string;
  platformId: string;
  /** Platform discriminator — added to the manual post on create. */
  platform: string;
  authorBasePath?: PostAuthorBasePath;

  /** Pass an existing post to edit; omit to create a new one. */
  post?: Post | null;

  /** Pre-populated topic tag (from "link as example for topic X" entry point). */
  initialExampleForTopicIds?: string[];

  /** Called after successful save — caller uses for refresh hooks. */
  onSaved?: (savedPostId: string) => void;
}

function authorPostToPostLike(
  post: Post,
  projectId: string,
  fallbackPlatform: string,
): PostLike {
  // Legacy scraped posts predate the unified Post.platforms[] field — fall
  // back to the contextual platform so the dialog's Save button (gated by
  // platforms.length > 0) is enabled.
  const platforms =
    post.platforms && post.platforms.length > 0
      ? post.platforms
      : fallbackPlatform
      ? [fallbackPlatform as any]
      : [];
  return {
    id: post.id,
    ownerKind: 'author_post_example',
    ownerPath: '',
    projectId,
    title: post.title ?? undefined,
    platforms,
    hook: post.hook ?? undefined,
    body: post.body ?? undefined,
    cta: post.cta ?? undefined,
    notes: post.notes ?? undefined,
    sourceUrl: post.sourceUrl ?? undefined,
    sourceCreatorHandle: post.sourceCreatorHandle ?? undefined,
    aiAssets: (post.aiAssets ?? undefined) as Record<string, any> | undefined,
    linkedTopicIds: post.exampleForTopicIds ?? undefined,
    addedBy: post.addedBy ?? undefined,
  };
}

function emptyPostLike(projectId: string, userId: string, platform: string): PostLike {
  return {
    id: '',
    ownerKind: 'author_post_example',
    ownerPath: '',
    projectId,
    platforms: platform ? [platform as any] : [],
    addedBy: userId,
  };
}

export const AuthorPostEditorDialog: React.FC<AuthorPostEditorDialogProps> = ({
  open, onClose, projectId, userId, authorId, platformId, platform,
  authorBasePath = 'authors', post, initialExampleForTopicIds, onSaved,
}) => {
  const corpusService =
    authorBasePath === 'external_authors'
      ? externalAuthorContentCorpusService
      : authorContentCorpusService;

  const initial = useMemo<PostLike | null>(() => {
    if (!open) return null;
    if (post) {
      const pl = authorPostToPostLike(post, projectId, platform);
      // Seed exampleForTopicIds[] from caller if creating-from-topic flow is used
      // even on existing post (idempotent — corpus service uses arrayUnion).
      if (initialExampleForTopicIds?.length) {
        pl.linkedTopicIds = Array.from(
          new Set([...(pl.linkedTopicIds ?? []), ...initialExampleForTopicIds]),
        );
      }
      return pl;
    }
    const empty = emptyPostLike(projectId, userId, platform);
    if (initialExampleForTopicIds?.length) {
      empty.linkedTopicIds = initialExampleForTopicIds;
    }
    return empty;
  }, [open, post, initialExampleForTopicIds, projectId, userId, platform]);

  const [savingId, setSavingId] = useState<string | null>(post?.id ?? null);

  const handleSave = useCallback(async (next: PostLike): Promise<string> => {
    if (post?.id) {
      await corpusService.updatePostContent({
        projectId,
        authorId,
        platformId,
        postId: post.id,
        patch: {
          hook: next.hook,
          body: next.body,
          cta: next.cta,
          notes: next.notes,
          title: next.title,
          sourceUrl: next.sourceUrl,
          sourceCreatorHandle: next.sourceCreatorHandle,
          aiAssets: next.aiAssets as any,
          platforms: (next.platforms ?? null) as any,
        },
      });
      // Sync example tags via dedicated method to keep lifecycleStage correct.
      const incoming = new Set(next.linkedTopicIds ?? []);
      const existing = new Set(post.exampleForTopicIds ?? []);
      for (const tid of incoming) {
        if (!existing.has(tid)) {
          await corpusService.addTopicExampleTag({
            projectId, authorId, platformId, postId: post.id, topicId: tid, addedBy: userId,
          });
        }
      }
      for (const tid of existing) {
        if (!incoming.has(tid)) {
          await corpusService.removeTopicExampleTag({
            projectId, authorId, platformId, postId: post.id, topicId: tid,
          });
        }
      }
      setSavingId(post.id);
      onSaved?.(post.id);
      return post.id;
    }
    const input: CreateManualPostInput = {
      projectId,
      authorId,
      platformId,
      platform,
      hook: next.hook,
      body: next.body,
      cta: next.cta,
      notes: next.notes,
      title: next.title,
      sourceUrl: next.sourceUrl,
      sourceCreatorHandle: next.sourceCreatorHandle,
      aiAssets: next.aiAssets as Record<string, unknown> | undefined,
      exampleForTopicIds: next.linkedTopicIds,
      addedBy: userId,
    };
    const newId = await corpusService.createManualPost(input);
    setSavingId(newId);
    onSaved?.(newId);
    return newId;
  }, [authorId, platformId, platform, projectId, userId, corpusService, post, onSaved]);

  const handleGenerateImage = useCallback(async (config: GenerateImageConfig): Promise<GenerateImageResult> => {
    const targetId = savingId ?? post?.id;
    if (!targetId) throw new Error('Save the post first to generate an image.');
    const client = new ContentPlanApiClient();
    const resp = await client.generateImage({
      projectId,
      userId,
      ownerKind: 'post' as any,
      ownerId: targetId,
      slotKey: config.slotKey,
      prompt: config.prompt,
      modelHint: config.model,
      aspectRatio: config.aspectRatio,
      style: config.style,
    } as any);
    const r = resp as any;
    if (!r || r.success === false) {
      return {
        success: false,
        error: (r?.error ?? 'image generation failed') as string,
      };
    }
    return {
      success: true,
      imageUrl: r.assetUrl ?? r.imageUrl,
      generationModel: r.generationModel ?? config.model,
    };
  }, [savingId, post, projectId, userId]);

  const handleUploadReference = useCallback(async (file: File) => {
    const targetId = savingId ?? post?.id ?? `pending_${Date.now()}`;
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `projects/${projectId}/${authorBasePath}/${authorId}/platforms/${platformId}/author_posts/${targetId}/ref-images/${Date.now()}_${sanitizedName}`;
    const storage = new StorageService();
    const result = await storage.uploadImage(path, file);
    return { url: result.assetUrl.origin, mimeType: file.type || 'image/png' };
  }, [savingId, post, projectId, authorBasePath, authorId, platformId]);

  if (!open || !initial) return null;

  return (
    <UnifiedPostDialog
      open={open}
      onClose={onClose}
      post={initial}
      onSave={handleSave}
      onGenerateImage={handleGenerateImage}
      onUploadReference={handleUploadReference}
    />
  );
};

export default AuthorPostEditorDialog;
